import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/provider/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/provider/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/provider/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/provider/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/provider/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/provider/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/provider/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/provider/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/provider/UPDATE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviders(options = {}){
  const { entityKey } = options

  return (dispatch, getState) => {
    const loadedForKeys = [...getState().providers.loadedForKeys]
    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/providers.json', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadProvider(providerId, options = {}){
  const { entityKey } = options

  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = [...state.providers.loadedIds]
    const loadedForKeys = [...state.providers.loadedForKeys]

    if (providerId && !loadedIds.includes(providerId)) loadedIds.push(providerId)
    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/providers/${providerId}.json`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createProvider(provider, options = {}){
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/providers.json', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function exportProvider(provider, options){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/providers/${provider.id}/export.json`, options, config)
      .then(() => {
        dispatch(updateSuccess())

        return { success: true }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateProvider(provider, options){
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/providers/${provider.id}.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function generateSupportToken(provider, options = {}){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/providers/${provider.id}/generate_support_token.json`, options, config)
      .then(({ data }) => {
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
